import { X } from 'lucide-react';
import { type ComponentType, useMemo, useState } from 'react';
import { type Control, useController } from 'react-hook-form';
import type { GroupBase, SingleValue, StylesConfig } from 'react-select';
import type { CreatableProps } from 'react-select/creatable';
import CreatableSelect from 'react-select/creatable';

import { useCreateTag, useIntl, useTagAutocomplete } from '../../hooks';
import type { Tag } from '../../types/tag.js';
import type { DeviceSettingsFormValues } from '../../validations/device-settings.js';
import { Badge } from '../ui';

const MAX_DEVICE_TAGS = 50;

interface DeviceTagsManagerProps {
  control: Control<DeviceSettingsFormValues>;
  disabled?: boolean;
}

interface TagOption {
  value: string;
  label: string;
  tagId?: string;
}

type CreatableSelectProps = CreatableProps<TagOption, false, GroupBase<TagOption>>;

const CreatableSelectComponent = CreatableSelect as unknown as ComponentType<CreatableSelectProps>;

const selectStyles: StylesConfig<TagOption, false, GroupBase<TagOption>> = {
  control: (provided, state) => ({
    ...provided,
    minHeight: 40,
    borderRadius: 6,
    borderColor: 'var(--color-border)',
    color: 'var(--color-foreground)',
    boxShadow: state.isFocused ? '0 0 0 1px #0f172a' : 'none',
    ':hover': {
      borderColor: 'var(--color-border)',
      cursor: 'pointer',
    },
    backgroundColor: state.isDisabled ? 'var(--color-background-dark)' : 'var(--color-input)',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 30,
    backgroundColor: 'var(--color-background-light)',
    border: '1px solid var(--color-border)',
  }),
  option: (provided) => ({
    ...provided,
    backgroundColor: 'var(--color-background-light)',
    color: 'var(--color-foreground)',
    ':hover': {
      backgroundColor: 'var(--color-background-dark)',
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: 'var(--color-foreground)',
  }),
};

export function DeviceTagsManager({ control, disabled = false }: DeviceTagsManagerProps) {
  const intl = useIntl();
  const {
    field: { value: formValue = [], onChange },
  } = useController({ name: 'tags', control });

  const tags: Tag[] = useMemo(() => (Array.isArray(formValue) ? formValue : []), [formValue]);
  const [inputValue, setInputValue] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  const createTag = useCreateTag();
  const suggestionsQuery = useTagAutocomplete(inputValue.trim(), 10, true);

  const assignedNames = useMemo(() => {
    return new Set(tags.map((tag) => tag.name.toLowerCase()));
  }, [tags]);

  const assignedIds = useMemo(() => new Set(tags.map((tag) => tag.id)), [tags]);

  const availableOptions: TagOption[] = useMemo(() => {
    const suggestionOptions = (suggestionsQuery.data ?? [])
      .filter((tag) => !assignedNames.has(tag.name.toLowerCase()))
      .map((tag) => ({ value: tag.name, label: tag.name, tagId: tag.id }));

    return suggestionOptions;
  }, [assignedNames, suggestionsQuery.data]);

  const resetSelect = () => {
    setInputValue('');
  };

  const handleInputChange: NonNullable<CreatableSelectProps['onInputChange']> = (
    nextValue,
    action,
  ) => {
    if (action.action === 'input-change') {
      setInputValue(nextValue);
    } else if (action.action === 'menu-close') {
      resetSelect();
    }
  };

  const maxReached = tags.length >= MAX_DEVICE_TAGS;

  const handleAddTag = (tag: Tag) => {
    if (maxReached) {
      setMessage(intl.formatMessage({ id: 'deviceTags.maxReached' }, { max: MAX_DEVICE_TAGS }));
      resetSelect();
      return;
    }
    if (assignedIds.has(tag.id)) {
      setMessage(intl.formatMessage({ id: 'deviceTags.alreadyAssigned' }, { name: tag.name }));
      resetSelect();
      return;
    }
    onChange([...tags, tag]);
    setMessage(null);
    resetSelect();
  };

  const handleSelect = (option: SingleValue<TagOption>) => {
    if (!option) return;

    if (option.tagId) {
      const suggestion = suggestionsQuery.data?.find((tag) => tag.id === option.tagId);
      if (suggestion) {
        handleAddTag(suggestion);
      } else {
        handleAddTag({ id: option.tagId, name: option.label });
      }
      return;
    }

    const normalized = option.value.toLowerCase();
    const suggestion = suggestionsQuery.data?.find((tag) => tag.name.toLowerCase() === normalized);
    if (suggestion) {
      handleAddTag(suggestion);
    }
  };

  const handleCreate = async (value: string) => {
    const name = value.trim();
    if (!name) return;
    if (maxReached) {
      setMessage(intl.formatMessage({ id: 'deviceTags.maxReached' }, { max: MAX_DEVICE_TAGS }));
      resetSelect();
      return;
    }
    if (assignedNames.has(name.toLowerCase())) {
      setMessage(intl.formatMessage({ id: 'deviceTags.alreadyAssigned' }, { name }));
      resetSelect();
      return;
    }

    try {
      const suggestion = suggestionsQuery.data?.find(
        (tag) => tag.name.toLowerCase() === name.toLowerCase(),
      );
      if (suggestion) {
        handleAddTag(suggestion);
        return;
      }

      const created = await createTag.mutateAsync({ name });
      handleAddTag(created);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : intl.formatMessage({ id: 'deviceTags.addError' });
      setMessage(msg);
    }
  };

  const handleRemove = (tagId: string) => {
    const next = tags.filter((tag) => tag.id !== tagId);
    onChange(next);
    setMessage(null);
  };

  return (
    <div className="space-y-3">
      {tags.length === 0 ? (
        <div className="text-sm text-foreground">
          {intl.formatMessage({ id: 'deviceTags.noTags' })}
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {tags.map((tag) => (
            <Badge
              key={tag.id}
              variant="secondary"
              className="flex items-center gap-1 pr-1 text-xs"
            >
              <span>{tag.name}</span>
              <button
                type="button"
                className="inline-flex h-4 w-4 items-center justify-center rounded-full transition-colors hover:bg-slate-200"
                aria-label={intl.formatMessage({ id: 'deviceTags.removeTag' }, { name: tag.name })}
                onClick={() => handleRemove(tag.id)}
                disabled={disabled}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      <CreatableSelectComponent
        inputValue={inputValue}
        onInputChange={handleInputChange}
        value={null}
        onChange={handleSelect}
        onCreateOption={(value) => {
          void handleCreate(value);
        }}
        isClearable
        classNamePrefix="device-tags-select"
        placeholder={intl.formatMessage({ id: 'deviceTags.placeholder' })}
        isDisabled={disabled || createTag.isPending || maxReached}
        isLoading={suggestionsQuery.isFetching || createTag.isPending}
        options={availableOptions}
        formatCreateLabel={(value: string) =>
          intl.formatMessage({ id: 'deviceTags.createLabel' }, { value })
        }
        components={{ IndicatorSeparator: null }}
        styles={selectStyles}
        inputId="device-tags-select"
        instanceId="device-tags-select"
        aria-label={intl.formatMessage({ id: 'deviceSettings.fields.tags' })}
      />

      {message ? <div className="text-sm text-destructive">{message}</div> : null}
      {maxReached ? (
        <div className="text-xs text-foreground">
          {intl.formatMessage({ id: 'deviceTags.maxReached' }, { max: MAX_DEVICE_TAGS })}
        </div>
      ) : null}
    </div>
  );
}
